import React, {FC, useContext, useEffect, useState} from 'react';
import {Icons, Images} from '../../../../assets';
import {Button, ButtonVariant} from '../../../../components/button';
import {ButtonLoader} from '../../../../components/button-loader';
import {AccountContext, AccountContextInterface} from '../../../../contexts';
import {Cred} from '../../../../models';
import {AccountOrganization} from '../../../../models/organization.model';
import {UserSkill} from '../../../../models/skill.model';
import FilterModal from '../filter-modal';
import {PublicCredCard} from './public-cred-card';

interface PublicCredSectionProps {
  accountId: string;
}

const PAGE_SIZE = 4;

export const PublicCredSection: FC<PublicCredSectionProps> = ({accountId}) => {
  const {accountService} = useContext<AccountContextInterface>(AccountContext);
  const [creds, setCreds] = useState<Cred[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [selectedOrganizations, setSelectedOrganizations] = useState<AccountOrganization[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<UserSkill[]>([]);
  const [organizationIds, setOrganizationIds] = useState<string[]>([]);
  const [skillIds, setSkillIds] = useState<string[]>([]);

  useEffect(() => {
    const fetchCreds = async () => {
      try {
        setLoading(true);
        const {data} = await accountService.getCreds(accountId, organizationIds, page, PAGE_SIZE, skillIds);
        if (data) {
          setCreds(prev => (page === 1 ? data : [...prev, ...data]));
          setHasMore(data.length === PAGE_SIZE);
        }
      } catch (error) {
        setCreds([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    };

    fetchCreds();
  }, [accountId, accountService, organizationIds, page, skillIds]);

  const toggleFilterModal = () => setFilterModalOpen(prev => !prev);

  const applyFilters = () => {
    const newOrgIds = selectedOrganizations.map(org => org.id);
    const newSkillIds = selectedSkills.map(skill => skill.id);
    setOrganizationIds(newOrgIds);
    setSkillIds(newSkillIds);
    setPage(1);
    toggleFilterModal();
  };

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      setPage(prev => prev + 1);
    }
  };

  const filterCount = selectedOrganizations.length + selectedSkills.length;

  return (
    <section className="flex flex-col gap-5">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={Icons.CRED_ICON} alt="" className="h-5 w-5" />
          <h2 className="text-2xl font-semibold text-gray-900">Creds</h2>
        </div>
        <button
          className="flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:border-gray-300"
          onClick={toggleFilterModal}>
          <img alt="" src={Icons.FILTER} className="h-4 w-4" />
          Filter
          {filterCount > 0 && (
            <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-600 px-1 text-xs font-semibold text-white">
              {filterCount}
            </span>
          )}
        </button>
      </header>

      {loading && creds.length === 0 ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <ButtonLoader />
        </div>
      ) : null}

      {!loading && creds.length === 0 ? (
        <div className="flex flex-col items-center gap-4 rounded-3xl border border-dashed border-gray-200 bg-white px-8 py-16 text-center text-gray-500">
          <img alt="" src={Images.NO_ACTIVITY} className="h-36 w-auto" />
          <p>This Aura has not added any creds yet.</p>
        </div>
      ) : null}

      {creds.length > 0 && (
        <div className="flex flex-col gap-6">
          {creds.map(cred => (
            <PublicCredCard key={cred.id} cred={cred} />
          ))}

          {hasMore && (
            <Button disabled={loading} onClick={handleLoadMore} variant={ButtonVariant.Outline}>
              {loading ? <ButtonLoader /> : 'View more'}
            </Button>
          )}
        </div>
      )}

      <FilterModal
        accountId={accountId}
        applyFilters={applyFilters}
        filterModalOpen={filterModalOpen}
        selectedOrganizations={selectedOrganizations}
        selectedSkills={selectedSkills}
        setSelectedOrganizations={setSelectedOrganizations}
        setSelectedSkills={setSelectedSkills}
        toggleFilterModal={toggleFilterModal}
      />
    </section>
  );
};
